"use client";

// NettiDock — netti's conversation, docked to the dashboard's bottom-right
// corner (law_netti_corner). It is ALWAYS there, just out of attention:
//   • PROXIMITY — the right edge is netti's field. As the pointer moves into it
//     `--np` (0→1) rises: a soft blur grows in from the edge, then the last
//     words reveal bottom-up. The more bottom-right the pointer goes, the
//     clearer netti becomes. Reach THE MARK (deep in the field) and the rail
//     arms itself — netti is there to talk to, no click, no button to hunt for.
//   • THE COMPOSER IS A FIELD TOO. Once the rail is up, the box you would type
//     in has its own generous nearness (left, above, and open to the right), so
//     a pointer resting anywhere near the text area keeps netti in focus even
//     when it is nowhere near the corner. The pure arithmetic lives in
//     ./dockField (tested); this file only measures and decides.
//   • CLICK or FOCUS anywhere in the rail commits it OPEN and persists it, so it
//     stays while you look elsewhere. The netti space PINS it fully open.
//   • FOCUS WINS. While the composer (or anything in the rail) has focus, no
//     pointer distance, pointerleave, window blur or timer can fold it. Esc and
//     the corner mark always can: those are the person asking.
//   • LEAVING settles it back into the corner: the frost thins first, the words
//     go last (`--np-text` trails `--np` on the way down), so it recedes rather
//     than disappearing. EVERY way it can fold by itself goes through ONE timer
//     (LEAVE_MS) and ONE guard (`canFold`, read live): never one pointer sample,
//     never a stale closure.
// ONE rAF loop eases both variables and writes them to :root, so the rail, the
// feather and the corner label all read the same nearness. The loop SUSPENDS
// itself at rest and a passive pointermove wakes it — nothing spins while netti
// is asleep in the corner. The pointer handler does no layout read: the
// composer's rect is measured on events (resize, opening, the textarea growing)
// and cached. The animated properties are opacity, a mask, and the words' light
// blur; the feather and frost radii are fixed.
//
// The rail also holds the PAST — every conversation is kept on the server and
// can be brought back (or forgotten) from a quiet row at the top; "new" starts
// a fresh thread; "sound" is the one-tap mute. netti itself can do all three
// when asked (its chat tools land as directives in the hook).
//
// A sheet over the room (html[data-sheet-open]) owns the pointer and Esc: the
// field pauses behind it and a keystroke meant for the sheet never reaches here.

import * as React from "react";
import { useRouter } from "next/navigation";
import NettiChat from "./NettiChat";
import { useNettiChat, type ConversationRow } from "../../lib/netti/useNettiChat";
import { useNettiPrefs } from "../../lib/netti/prefs";
import { playNettiSound } from "../../lib/netti/sound";
import { performExpression } from "../../lib/netti/expression";
import { humanTime } from "../../lib/netti/when";
import OrganismMark from "../brand/OrganismMark";
import NettiUiBridge from "./NettiUiBridge";
import { NETTI_ASK_EVENT, cleanAsk, type NettiAskDetail } from "./nettiAsk";
import { MARK_OFF, MARK_ON, RISING, SUPPRESS_OFF, canFold, clarity, proximity, type Box, type FoldGuard } from "./dockField";
import { PLACES } from "../../lib/os/places";

const OPEN_KEY = "innernet:netti-dock-open";
const LEAVE_MS = 420; // every self-fold waits this beat, then re-reads the room

const sheetIsOpen = (): boolean => document.documentElement.hasAttribute("data-sheet-open");

export default function NettiDock({ preview = false }: { preview?: boolean } = {}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  // PINNED = opened by the netti space itself (the conversation IS the space) —
  // it stays fully open and never fades on proximity.
  const [pinned, setPinned] = React.useState(false);
  // NEAR = armed by the pointer alone (past the mark). Transient: it is never
  // persisted, and stepping out of the field lets it go.
  const [near, setNear] = React.useState(false);
  // WANT FOCUS = somebody asked for the composer (the mark, the netti space, a
  // conversation coming back). A rail restored open from the session, or
  // brushed by the pointer, reveals netti; it never takes your keyboard.
  const [wantFocus, setWantFocus] = React.useState(false);
  const chat = useNettiChat();
  const lastMsg = chat.msgs[chat.msgs.length - 1];
  const offered = lastMsg && lastMsg.who === "netti" ? (lastMsg.replies ?? []) : [];
  const [prefs, setPrefs] = useNettiPrefs();
  const router = useRouter();
  const railRef = React.useRef<HTMLElement>(null);
  const cornerRef = React.useRef<HTMLButtonElement>(null);
  const foldTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const npRef = React.useRef(0);      // eased --np actually written to :root
  const npTextRef = React.useRef(0);  // the words — trails --np on the way out
  const proxRef = React.useRef(0);    // raw pointer nearness, 0→1
  const nearRef = React.useRef(false);
  // set when the rail is folded BY HAND while the pointer is still in the
  // field; cleared as soon as the pointer steps back out.
  const suppressRef = React.useRef(false);
  const openRef = React.useRef(false);
  const pinnedRef = React.useRef(false);
  const upRef = React.useRef(false);
  // the unsent words, kept here as they change so a fold can ask "is there a
  // draft?" without reaching into a textarea (which the past view unmounts).
  const draftRef = React.useRef("");
  const busyRef = React.useRef(false);
  const viewRef = React.useRef<"chat" | "past">("chat");

  // the rail is up whenever anything holds it there — pointer, click, or pin.
  const up = open || pinned || near;

  // ── the past — the rail's second view ─────────────────────────────────────
  const [view, setView] = React.useState<"chat" | "past">("chat");
  // the past view unmounts the conversation, so the words in the composer are
  // taken out of it on the way and handed back when it returns.
  const [resumeDraft, setResumeDraft] = React.useState("");
  const [past, setPast] = React.useState<ConversationRow[]>([]);
  const [pastBusy, setPastBusy] = React.useState(false);
  // the hook hands back a fresh object every render, so depend on the pieces
  // (each is stable): depending on `chat` itself re-ran this effect on every
  // render and fetched the list in a loop while the past view was open.
  const { listConversations, forgetConversation, openConversation } = chat;
  const pastReq = React.useRef(0);
  const loadPast = React.useCallback(async () => {
    const req = ++pastReq.current;
    setPastBusy(true);
    const rows = await listConversations();
    if (req !== pastReq.current) return; // a newer ask is already on its way
    setPast(rows);
    setPastBusy(false);
  }, [listConversations]);
  React.useEffect(() => {
    if (view === "past") void loadPast();
  }, [view, loadPast]);
  React.useEffect(() => {
    // netti forgot / cleared / opened something — keep the list honest
    const on = () => { if (view === "past") void loadPast(); };
    window.addEventListener("innernet:netti-conversations", on);
    return () => window.removeEventListener("innernet:netti-conversations", on);
  }, [view, loadPast]);
  // a conversation directive lands as a swap back to the words
  React.useEffect(() => {
    if (chat.streamingId) setView("chat");
  }, [chat.streamingId]);

  const busy = chat.typing || !!chat.streamingId;

  // the refs the pointer handlers and the timer read LIVE — they must never see
  // the render a closure was made in. Written after every commit, before paint.
  React.useLayoutEffect(() => {
    openRef.current = open;
    pinnedRef.current = pinned;
    nearRef.current = near;
    upRef.current = up;
    viewRef.current = view;
    busyRef.current = busy;
  });

  // netti chat is hidden on mobile for now (to be reintroduced) — the dock's
  // proximity model has no touch equivalent, so under 720px it renders nothing.
  // 720 is the CSS breakpoint too (the corner and the full-width rail): the two
  // used to disagree between 641 and 720px.
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  React.useEffect(() => {
    try { setOpen(sessionStorage.getItem(OPEN_KEY) === "1"); } catch { /* private mode */ }
  }, []);

  const setOpenPersist = React.useCallback((v: boolean) => {
    setOpen(v);
    try { sessionStorage.setItem(OPEN_KEY, v ? "1" : "0"); } catch { /* private mode */ }
  }, []);

  // ── ONE way to fold by itself ──────────────────────────────────────────────
  // Whatever notices that netti might be done (the pointer field letting go, the
  // pointer leaving the rail or the page, the window losing focus, focus leaving
  // the rail, an answer finishing) calls scheduleFold(). Nothing folds on that
  // signal: it only starts a beat. When the beat ends the room is read AGAIN,
  // live, through refs (never the closure the timer was made in): is focus in the
  // rail, is there a draft, is netti busy, is it the past view, is the pointer
  // still near, is a sheet up? Only if the answer to all of them is no does the
  // rail settle. The person asking (Esc, the mark) is collapse(), below.
  const cancelFold = React.useCallback(() => {
    if (foldTimer.current) { clearTimeout(foldTimer.current); foldTimer.current = null; }
  }, []);

  const readGuard = React.useCallback((): FoldGuard => ({
    focusedWithin: !!railRef.current?.contains(document.activeElement),
    draft: draftRef.current,
    busy: busyRef.current,
    view: viewRef.current,
    pinned: pinnedRef.current,
  }), []);

  // the pointer is still on netti: on the rail or the mark, or inside the field.
  const pointerHolds = React.useCallback((): boolean => {
    if (proxRef.current >= MARK_OFF) return true;
    return !!railRef.current?.matches(":hover") || !!cornerRef.current?.matches(":hover");
  }, []);

  const settle = React.useCallback(() => {
    setNear(false);
    setWantFocus(false);
    setOpenPersist(false);
  }, [setOpenPersist]);

  const scheduleFold = React.useCallback(() => {
    if (foldTimer.current || pinnedRef.current) return;
    foldTimer.current = setTimeout(() => {
      foldTimer.current = null;
      if (!upRef.current || sheetIsOpen()) return;
      if (!canFold(readGuard()) || pointerHolds()) return;
      settle();
    }, LEAVE_MS);
  }, [readGuard, pointerHolds, settle]);

  React.useEffect(() => () => cancelFold(), [cancelFold]);

  // an answer finishing may be the last thing keeping the rail up (the person
  // clicked away mid-stream): look again once netti is done.
  const wasBusy = React.useRef(false);
  React.useEffect(() => {
    if (wasBusy.current && !busy && upRef.current) scheduleFold();
    wasBusy.current = busy;
  }, [busy, scheduleFold]);

  // the person folds it: the mark, or Esc. This never asks the guard, and it
  // holds the field off until the pointer steps out of the mark's neighbourhood
  // (otherwise the very next move, with the cursor still parked there, would
  // raise it again). If the keyboard was inside the rail, it goes to the mark.
  const collapse = React.useCallback(() => {
    cancelFold();
    const hadFocus = !!railRef.current?.contains(document.activeElement);
    setPinned(false);
    setNear(false);
    setWantFocus(false);
    proxRef.current = 0;
    suppressRef.current = true;
    setOpenPersist(false);
    if (hadFocus) cornerRef.current?.focus({ preventScroll: true });
  }, [cancelFold, setOpenPersist]);

  const openByHand = React.useCallback(() => {
    cancelFold();
    setWantFocus(true);
    setOpenPersist(true);
  }, [cancelFold, setOpenPersist]);

  // a click or focus inside the rail commits it open — the same as the mark does
  // — so it stays while you look elsewhere. No focus request: you are already in.
  const commit = React.useCallback(() => {
    cancelFold();
    if (!openRef.current && !pinnedRef.current) setOpenPersist(true);
  }, [cancelFold, setOpenPersist]);

  // netti wakes as the rail comes up — once per visit, and only when the
  // person can hear/see it (their presence settings).
  const greetedRef = React.useRef(false);
  React.useEffect(() => {
    if (!up || greetedRef.current || preview) return;
    greetedRef.current = true;
    if (prefs.expressions && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) performExpression("wake", { intensity: 0.45, sound: false });
    if (prefs.sounds) playNettiSound("hello");
  }, [up, preview, prefs.expressions, prefs.sounds]);

  // escape settles the rail away — unless something else is the top layer: a
  // sheet owns Esc while it is up, a keystroke another handler already used
  // (stopping netti mid-answer, closing a menu) is spent, and an IME cancelling
  // its composition is not talking to us.
  React.useEffect(() => {
    if (!up) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
      if (sheetIsOpen()) return;
      collapse();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [up, collapse]);

  // entering the netti space (or the mobile top-bar icon) opens AND pins it.
  React.useEffect(() => {
    const onOpen = (e: Event) => {
      const on = (e as CustomEvent<{ on?: boolean }>).detail?.on !== false;
      setPinned(on);
      setWantFocus(on);
      setOpenPersist(on);
    };
    window.addEventListener("innernet:netti-open", onOpen);
    return () => window.removeEventListener("innernet:netti-open", onOpen);
  }, [setOpenPersist]);

  // the netti space asks for a past conversation → open the rail on it
  React.useEffect(() => {
    const onOpenConv = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id;
      if (!id) return;
      setView("chat");
      setWantFocus(true);
      setOpenPersist(true);
      void openConversation(id);
    };
    window.addEventListener("innernet:netti-open-conversation", onOpenConv);
    return () => window.removeEventListener("innernet:netti-open-conversation", onOpenConv);
  }, [openConversation, setOpenPersist]);

  // somewhere on the page asks netti something (askNetti, ./nettiAsk): open the
  // rail on the conversation and send it as the person's own words. No parallel
  // chat: the same thread, the same transport, the same rate ladder. The event
  // carries its answer back (`outcome`) so the asker can say what came of it. A
  // phone has no rail, so nobody answers there; an ask never interrupts an answer
  // that is still being written, and never goes out while netti is resting or
  // not public yet: the rail opens and says so itself.
  const { send: sendChat, resting: chatResting } = chat;
  React.useEffect(() => {
    const onAsk = (e: Event) => {
      const detail = (e as CustomEvent<NettiAskDetail>).detail;
      const text = cleanAsk(detail?.text);
      if (!detail || !text || narrow) return;
      cancelFold();
      setView("chat");
      setWantFocus(true);
      setOpenPersist(true);
      if (preview || chatResting) detail.outcome = "opened";
      else if (busyRef.current) detail.outcome = "busy";
      else {
        detail.outcome = "sent";
        void sendChat(text);
      }
    };
    window.addEventListener(NETTI_ASK_EVENT, onAsk);
    return () => window.removeEventListener(NETTI_ASK_EVENT, onAsk);
  }, [sendChat, chatResting, preview, narrow, cancelFold, setOpenPersist]);

  // a real page outside the shell (account, docs, developers…) — netti's
  // open_page lands here; the router carries the person there.
  React.useEffect(() => {
    const onHref = (e: Event) => {
      const href = (e as CustomEvent<{ href?: string }>).detail?.href;
      // an in-site path only: one slash, never `//host` or `/\host` (an off-site url in disguise)
      if (!href || !/^\/(?![\/\\])/.test(href)) return;
      router.push(href);
    };
    window.addEventListener("innernet:netti-href", onHref);
    return () => window.removeEventListener("innernet:netti-href", onHref);
  }, [router]);

  // the open rail owns the corner — the Noticer pill steps aside (CSS hook).
  // A persistent `data-netti-corner` marks that the always-there corner affordance
  // exists (desktop only), so the Noticer can seat itself to ITS LEFT.
  React.useEffect(() => {
    const root = document.documentElement;
    if (up) root.setAttribute("data-netti-rail", "");
    else root.removeAttribute("data-netti-rail");
    return () => root.removeAttribute("data-netti-rail");
  }, [up]);
  React.useEffect(() => {
    const root = document.documentElement;
    if (!narrow) root.setAttribute("data-netti-corner", "");
    else root.removeAttribute("data-netti-corner");
    return () => root.removeAttribute("data-netti-corner");
  }, [narrow]);

  // ── ONE eased loop → --np (+ --np-text) on :root ───────────────────────────
  // --np is nearness: the rail's opacity, its bottom-up mask, the feather, and
  // the corner label's retirement all key off it in globals.css. --np-text is
  // the same value with a LAZIER fall, so on the way out the frost thins first
  // and the words are the last thing to go.
  // The loop parks itself when everything has settled at zero; a passive
  // pointermove (or an open/pin) wakes it, so an idle dashboard runs no rAF.
  const rafRef = React.useRef(0);
  const wakeRef = React.useRef<() => void>(() => {});
  React.useEffect(() => {
    const root = document.documentElement;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const tick = () => {
      // committed or pinned: fully there. Otherwise nearness, and once the
      // pointer has armed it the whole near-zone reads in focus (`clarity`).
      const target = openRef.current || pinnedRef.current ? 1 : clarity(proxRef.current, nearRef.current);
      // it arrives gently and sets back a touch faster than it came
      const rising = target > npRef.current;
      const k = reduce ? 1 : rising ? 0.16 : 0.24;
      npRef.current += (target - npRef.current) * k;
      if (Math.abs(target - npRef.current) < 0.002) npRef.current = target;

      // the words: same climb, half the descent — they linger a beat behind.
      const kt = reduce ? 1 : target > npTextRef.current ? 0.16 : 0.1;
      npTextRef.current += (target - npTextRef.current) * kt;
      if (Math.abs(target - npTextRef.current) < 0.002) npTextRef.current = target;

      root.style.setProperty("--np", npRef.current.toFixed(3));
      root.style.setProperty("--np-text", npTextRef.current.toFixed(3));

      // CONVERGED → park the loop. Nothing can change the target except a
      // pointermove, an open/pin, or the field emptying — and every one of those
      // calls wake() — so a cursor resting anywhere (in the field, at the corner,
      // or across the room) costs no frames at all.
      if (npRef.current === target && npTextRef.current === target) {
        rafRef.current = 0;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const wake = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    };
    wakeRef.current = wake;
    wake();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      root.style.removeProperty("--np");
      root.style.removeProperty("--np-text");
    };
  }, []);

  // ── the composer, as a field ──────────────────────────────────────────────
  // The text area's rect is measured ON EVENTS — the rail opening, a resize, the
  // textarea growing a line — and cached; the pointer handler only reads the
  // cache. (It is a fixed corner of a fixed rail, so it moves for no other reason.)
  const composerBoxRef = React.useRef<Box | null>(null);
  const composerDirty = React.useRef(true);
  const composerBox = React.useCallback((): Box | null => {
    if (composerDirty.current) {
      composerDirty.current = false;
      const el = railRef.current?.querySelector<HTMLElement>(".nchat-input");
      const r = el?.getBoundingClientRect();
      composerBoxRef.current = r && r.width > 0 ? { left: r.left, top: r.top, right: r.right, bottom: r.bottom } : null;
    }
    return composerBoxRef.current;
  }, []);
  React.useEffect(() => {
    composerDirty.current = true;
    const el = railRef.current?.querySelector<HTMLElement>(".nchat-input");
    if (!el) return;
    const dirty = () => { composerDirty.current = true; };
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(dirty);
    ro?.observe(el);
    window.addEventListener("resize", dirty, { passive: true });
    return () => { ro?.disconnect(); window.removeEventListener("resize", dirty); };
  }, [view, narrow, preview, up]);

  // ── the pointer field → proximity ─────────────────────────────────────────
  // Passive pointermove; the handler stores a number, may flip one flag, and
  // wakes the loop — no layout reads (the composer rect is cached above), no
  // style writes, nothing that could stall a scroll. Touch/coarse pointers never
  // arm this (there is no "near" without a cursor).
  React.useEffect(() => {
    if (narrow) return;
    if (!window.matchMedia("(hover: hover)").matches) return;
    const root = document.documentElement;

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      // a sheet owns the pointer: nothing arms behind it.
      if (root.hasAttribute("data-sheet-open")) return;
      const t = e.target;
      const inside = t instanceof Node && (!!railRef.current?.contains(t) || !!cornerRef.current?.contains(t));
      // the composer speaks once the rail is up or on its way (np > RISING)
      const composerLive = upRef.current || npRef.current > RISING;
      const prox = proximity({
        x: e.clientX,
        y: e.clientY,
        w: window.innerWidth,
        h: window.innerHeight,
        composer: composerLive ? composerBox() : null,
        composerLive,
        inside,
      });
      // a deliberate fold (the mark, or escape) holds the field off until the
      // pointer actually steps back out — otherwise the very next move, with
      // the cursor still parked on the corner, would raise it again.
      if (suppressRef.current) {
        if (prox >= SUPPRESS_OFF) {
          proxRef.current = 0;
          wakeRef.current();
          return;
        }
        suppressRef.current = false;
      }
      proxRef.current = prox;

      // the mark — cross it and netti is simply there. Letting go is not a
      // decision made on this sample: far from both fields only STARTS the beat
      // (scheduleFold), a return to either cancels it, and the guard reads the
      // room again when it ends.
      if (!nearRef.current && prox >= MARK_ON) {
        cancelFold();
        setNear(true);
      } else if (upRef.current) {
        if (prox < MARK_OFF) scheduleFold();
        else cancelFold();
      }
      wakeRef.current();
    };
    // pointer off the window entirely → the field empties. `pointerleave` on the
    // ROOT only fires when the pointer actually leaves the page — `pointerout`
    // bubbles from every element boundary crossed inside it, which would empty
    // the field mid-approach. Both this and a window blur only START the beat:
    // a focused composer or a draft rides through them (alt-tabbing away from a
    // half-written question must not fold it).
    const onLeave = () => {
      proxRef.current = 0;
      if (upRef.current) scheduleFold();
      wakeRef.current();
    };
    // a sheet rising retires a rail that was only on its way; one that is up
    // looks again when the sheet closes (nothing folds while it is open).
    const onSheet = () => {
      if (root.hasAttribute("data-sheet-open")) {
        if (!upRef.current) proxRef.current = 0;
      } else if (upRef.current) {
        scheduleFold();
      }
      wakeRef.current();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    root.addEventListener("pointerleave", onLeave, { passive: true });
    window.addEventListener("blur", onLeave);
    const mo = new MutationObserver(onSheet);
    mo.observe(root, { attributes: true, attributeFilter: ["data-sheet-open"] });
    return () => {
      window.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
      mo.disconnect();
    };
  }, [narrow, composerBox, cancelFold, scheduleFold]);

  // an open/pin (or letting go of one) has to restart a parked loop.
  React.useEffect(() => { wakeRef.current(); }, [open, pinned, near]);

  // the pointer on the rail keeps it; off it, a beat starts. Moving from the
  // composer onto the mark (a sibling of the rail, but part of it: it is the
  // fold control) is not leaving.
  const onRailLeave = React.useCallback((e: React.PointerEvent) => {
    if (e.relatedTarget instanceof Node && cornerRef.current?.contains(e.relatedTarget)) return;
    scheduleFold();
  }, [scheduleFold]);
  const onMarkLeave = React.useCallback((e: React.PointerEvent) => {
    if (e.relatedTarget instanceof Node && railRef.current?.contains(e.relatedTarget)) return;
    scheduleFold();
  }, [scheduleFold]);
  // focus moving from one thing in the rail to another is not leaving either
  const onRailBlur = React.useCallback((e: React.FocusEvent) => {
    if (e.relatedTarget instanceof Node && railRef.current?.contains(e.relatedTarget)) return;
    scheduleFold();
  }, [scheduleFold]);

  const onDraft = React.useCallback((v: string) => { draftRef.current = v; }, []);

  const forget = React.useCallback(async (id: string) => {
    if (await forgetConversation(id)) setPast((rows) => rows.filter((r) => r.id !== id));
  }, [forgetConversation]);

  // mobile: no netti chat surface at all for now (all hooks run above this line).
  // The UI bridge still stands: a pill netti left earlier opens its page in a
  // tab through it, and it renders nothing until something is asked of it.
  if (narrow) return <NettiUiBridge />;

  return (
    <>
      {/* netti's hands on the SCREEN: the connect window, a panel's tab, the
          import window, a page in a new tab (see NettiUiBridge). Mounted once. */}
      <NettiUiBridge />
      {/* the ONE corner affordance — always there at the bottom-right: "ask netti"
          + netti's mark. The label speaks only when the Noticer is quiet — while
          the corner is narrating a hint, the words retire and the mark alone
          holds the spot (`:root[data-noticer-live]`). When the rail is open only
          the persistent mark remains (tap it to fold back). */}
      <button
        ref={cornerRef}
        type="button"
        className="netti-corner"
        data-open={up ? "true" : undefined}
        aria-label={up ? "collapse netti" : "ask netti"}
        /* the mark is where "open" and "fold" meet. A pointer that merely
           arrived (near) has only revealed netti: clicking the mark then means
           "yes, talk" — commit it open and take the composer. Once it is held
           open (a click, focus, the netti space) the same mark folds it. */
        onClick={() => (open || pinned ? collapse() : openByHand())}
        onPointerEnter={cancelFold}
        onPointerLeave={onMarkLeave}
      >
        <span className="netti-corner-label" aria-hidden>ask netti</span>
        <span className="netti-corner-mark" aria-hidden><OrganismMark size={24} /></span>
      </button>

      <aside
        ref={railRef}
        className="netti-rail"
        data-open={up ? "true" : undefined}
        aria-hidden={!up}
        /* folded, the rail is out of the tab order and the accessibility tree
           (the composer is NOT disabled to get that: a disabled field drops the
           focus and the caret of someone mid-sentence). Esc lands here too: the
           rail goes inert, and the focus it held goes to the mark. */
        inert={!up ? true : undefined}
        onPointerEnter={cancelFold}
        onPointerLeave={onRailLeave}
        onPointerDown={commit}
        onFocus={commit}
        onBlur={onRailBlur}
      >
        {/* the corner mark is the ONE control for open/fold, and the rail is
            just the words — plus this one quiet row: the past (every
            conversation, kept), a fresh start, and the mute. The conversation +
            composer stay mounted so any half-written draft survives a fade.
            FOCUS is only ever taken by a deliberate open or a pin — walking past
            the corner reveals netti, it never steals your keyboard. */}
        {preview ? (
          // PREVIEW — netti isn't public yet. The corner and the rail are the
          // same everywhere, but here netti only says so: one message, no
          // composer, nothing to type into. The person met netti in their
          // first-run walk; this keeps that meeting honest until launch.
          <NettiChat
            msgs={[{
              id: "soon",
              who: "netti",
              // SHORT. This is the default a person meets by brushing past the
              // corner, not something they asked for — and it was four sentences
              // recapping a walk they had just taken. A line you can read on the
              // way past is the whole job; anything longer is netti holding the
              // door shut to explain why it's shut.
              text: "i can't talk from every corner yet — soon. until then i'm here, growing with everything you keep.",
            }]}
            rightHand
          />
        ) : (
          <>
            <div className="netti-rail-tools" data-up={up ? "true" : undefined}>
              <button
                type="button"
                className="netti-rail-tool"
                aria-pressed={view === "past"}
                tabIndex={up ? 0 : -1}
                onClick={() => {
                  if (view === "chat") setResumeDraft(draftRef.current);
                  setView((v) => (v === "past" ? "chat" : "past"));
                }}
              >
                {view === "past" ? "back" : "past"}
              </button>
              {view === "chat" && chat.msgs.length > 0 && (
                <button type="button" className="netti-rail-tool" tabIndex={up ? 0 : -1} onClick={() => chat.clearChat()} title="start a fresh conversation — this one stays in the past">
                  new
                </button>
              )}
              <button
                type="button"
                className="netti-rail-tool"
                aria-pressed={prefs.sounds}
                tabIndex={up ? 0 : -1}
                onClick={() => setPrefs({ sounds: !prefs.sounds })}
                title={prefs.sounds ? "netti makes its small sounds — tap to mute" : "netti is muted — tap for sound"}
              >
                {prefs.sounds ? "sound on" : "sound off"}
              </button>
              {chat.conversationTitle && view === "chat" && (
                <span className="netti-rail-title" title="this conversation">{chat.conversationTitle}</span>
              )}
            </div>
            {view === "past" ? (
              <div className="nchat-past" role="list" aria-label="past conversations">
                <div className="nchat-past-head">past conversations</div>
                {pastBusy && past.length === 0 && <div className="nchat-past-empty">looking…</div>}
                {!pastBusy && past.length === 0 && <div className="nchat-past-empty">nothing yet — this is the first.</div>}
                {past.map((c) => (
                  <div key={c.id} role="listitem" className={`nchat-past-row${c.id === chat.conversationId ? " is-current" : ""}`}>
                    <button
                      type="button"
                      className="nchat-past-open"
                      onClick={() => { void chat.openConversation(c.id); setView("chat"); }}
                      title={c.preview || c.title}
                    >
                      <span className="nchat-past-title">{c.title || "a conversation"}</span>
                      <span className="nchat-past-meta">
                        {c.turn_count} {c.turn_count === 1 ? "turn" : "turns"} · {humanTime(c.updated_at) || "just now"}
                        {c.id === chat.conversationId ? " · here" : ""}
                      </span>
                    </button>
                    <button type="button" className="nchat-past-forget" onClick={() => void forget(c.id)} aria-label={`forget "${c.title}"`}>
                      forget
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <NettiChat
                msgs={chat.msgs}
                typing={chat.typing}
                status={chat.status}
                streamingId={chat.streamingId}
                onSend={chat.send}
                onStop={chat.stop}
                // the replies netti offers ride its newest answer, and only while
                // that answer is the last word; a tap sends one as your words,
                // marked so netti's own shelf learns which ones you reach for
                replies={offered}
                onReply={(text) => chat.send(text, { via: "chip" })}
                // resting gates the composer — but never one that has focus or
                // words in it (NettiChat holds that line itself); folded, the
                // rail is inert instead, so a disabled field never drops a caret.
                disabled={chat.resting}
                placeholder={chat.resting ? "netti is resting…" : "ask what it remembers…"}
                autoFocus={wantFocus}
                initialDraft={resumeDraft}
                onDraftChange={onDraft}
                // the one real composer keeps its unsent line across a reload (lib/os/places)
                resumeKey={PLACES.nettiDraft()}
                rightHand
              />
            )}
          </>
        )}
      </aside>
    </>
  );
}
